import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

/**
 * What the autopilot would write next, and what is wrong with it — shown where
 * the switch is.
 *
 * The platform could already answer "what would run next" (the CLI's
 * `autopilot plan`, and the Test button on the Integrations card). Neither was
 * on this page, and more importantly neither answered the question that
 * actually matters, which is not WHAT but WHETHER IT IS READY. On 2026-08-08 a
 * manual read of the queue found three problems that every existing surface
 * reported as green: nine of twenty candidates had no PAA questions at all,
 * every one of them had zero linked secondaries while sitting on 5-24 orphaned
 * siblings, and `כאב גב תחתון סרטן` was eligible at position 17.
 *
 * So this panel loads by default and costs nothing — it is the deterministic
 * check, straight from the database. The model review is a separate button
 * because it spends money, and a panel that quietly bills every time the page
 * is opened is a panel people close.
 */

type Severity = "block" | "warn" | "note";
type Verdict = "ready" | "review" | "hold";

interface Finding {
  code: string;
  severity: Severity;
  summary: string;
  detail?: string;
  fix?: string;
}

interface Sibling {
  id: string;
  keyword: string;
  volume: number | null;
}

interface PreflightCandidate {
  keywordId: string;
  keyword: string;
  clusterId: string | null;
  volume: number;
  paaCount: number;
  competitorUrls: string[];
  linkedSecondaries: number;
  unlinkedSiblings: Sibling[];
  findings: Finding[];
  verdict: Verdict;
}

interface CandidateReview {
  keyword: string;
  verdict: "produce" | "fix-first" | "do-not-autopilot";
  reason: string;
  seoRisks: string[];
  angle: string | null;
  foldAsSecondary: { keyword: string; reason: string }[];
  separateArticle: { keyword: string; reason: string }[];
  suggestedFaq: string[];
  safetyNote: string | null;
}

interface PreflightResponse {
  ok: true;
  wanted: number;
  runLimit: number;
  report: {
    generatedAt: string;
    blocked: string | null;
    candidates: PreflightCandidate[];
    counts: Record<Verdict, number>;
    byCode: Record<string, number>;
  };
  skippedByReason: Record<string, number>;
  review?: {
    reviews: CandidateReview[];
    batchNote: string | null;
    modelUsed: string;
    provider: string;
    region: string | null;
    estimatedCostUsd: number | null;
    droppedSuggestions: string[];
    warnings: string[];
  } | null;
  reviewError?: string | null;
}

export function PreflightPanel() {
  const [withReview, setWithReview] = useState(false);

  const { data, isFetching, error, refetch } = useQuery<PreflightResponse>({
    queryKey: ["autopilot-preflight", withReview],
    queryFn: async () => {
      const r = await fetch(`/api/automations/autopilot/preflight?limit=20${withReview ? "&review=1" : ""}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    // The model pass is slow and paid — never re-fire it on a window focus.
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const report = data?.report;
  const holds = report?.candidates.filter((c) => c.verdict === "hold") ?? [];

  return (
    <div className="mt-3 rounded border border-slate-200 bg-white p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-800">Pre-flight</span>
        {report && (
          <>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">
              {report.counts.ready} ready
            </span>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
              {report.counts.review} review
            </span>
            <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-700">
              {report.counts.hold} hold
            </span>
          </>
        )}
        <button
          type="button"
          disabled={isFetching}
          onClick={() => void refetch()}
          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {isFetching ? "Checking…" : "Re-check"}
        </button>
        <button
          type="button"
          disabled={isFetching}
          onClick={() => setWithReview(true)}
          title="One model call for the whole batch, via whichever provider CLAUDE_PROVIDER points at. Costs a few cents. Results land in Tasks."
          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {withReview ? "AI review on" : "Review with AI"}
        </button>
        <a href="#/tasks" className="ml-auto font-medium text-blue-700 hover:underline">
          Open in Tasks →
        </a>
      </div>

      {/*
        Deliberately a SUMMARY and nothing more. This panel used to render every
        candidate with every finding expanded, and on a settings page that is a
        wall of scroll between the operator and the switch they came here for.
        The findings themselves live in the tasks inbox, where they are one row
        per keyword and can be acted on; here the only question is "is it safe to
        press Run now", which three numbers and the hold list answer.
      */}
      {error && <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700">{String(error)}</div>}

      {report?.blocked ? (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800">{report.blocked}</div>
      ) : (
        <p className="mt-1 text-slate-500">
          The next {data?.wanted ?? 20} candidates, checked before anything is spent. Only the top{" "}
          {data?.runLimit ?? 2} run next. This reports; it does not block.
        </p>
      )}

      {data?.reviewError && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
          AI review unavailable: {data.reviewError}. The checks are unaffected.
        </div>
      )}

      {data?.review && (
        <div className="mt-2 text-slate-500">
          Reviewed by {data.review.modelUsed} via {data.review.provider}
          {data.review.region ? ` (${data.review.region})` : ""} · $
          {(data.review.estimatedCostUsd ?? 0).toFixed(4)} — findings are in Tasks.
        </div>
      )}

      {holds.length > 0 && (
        <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-red-800">
          <span className="font-medium">
            {holds.length} on hold — {holds.length === 1 ? "it" : "they"} should not be written unattended:
          </span>
          <ul className="mt-1 space-y-0.5">
            {holds.map((c) => (
              <li key={c.keywordId}>
                <a href={`#/tasks?open=${encodeURIComponent(c.keywordId)}`} className="underline">
                  {c.keyword}
                </a>{" "}
                <span className="opacity-70">· {c.findings.find((f) => f.severity === "block")?.summary}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
